function getUrlOrigin(url) {
  if (!url) return ''
  try {
    return new URL(url).origin
  } catch {
    return ''
  }
}

// FE-3：endpoint 只留 path、去 query（new URL().pathname 天生不含 query/hash）、統一前導 /；
// 另去掉前兩段（版本號 + clientId，如 /v2/A1/self → /self），避免同一支 API 因 clientId 不同被拆成多個 issue。
// 已匯出共用：plugins/01.request.js 的 MalformedApiResponse 沿用同一套去敏／正規化規則。
export function getUrlEndpointPath(url) {
  if (!url) return ''
  try {
    const { pathname } = new URL(url)
    const parts = pathname.split('/').filter(Boolean)
    return '/' + parts.slice(2).join('/')
  } catch {
    return ''
  }
}

// FE-5：依正規化後的 path 標記業務類型，供看板篩選與 FE-2 分級 sample 使用
// 第一段以「(可選字首-)login」比對，涵蓋 login／guest-login／app-login 等變體，
// 避免 login 類型端點被漏判成 other 而被 FE-2 的 sample=0.4 隨機丟掉（違反「login 不能被抽掉」的目的）
function classifyErr998Endpoint(normalizedPath) {
  if (/^\/self(\/|$)/.test(normalizedPath)) return 'self'
  if (/^\/[a-z]*-?login(\/|$)/i.test(normalizedPath)) return 'login'
  return 'other'
}

export function getErr998SentryExtra({ endpoint, method, clientId, uidSite, clientIp } = {}) {
  const normalizedEndpoint = getUrlEndpointPath(endpoint)
  return {
    code: 'ERR_998',
    endpoint: normalizedEndpoint,
    method: method ?? '',
    site: clientId ?? '',
    ip: clientIp ?? '',
    apiHost: getUrlOrigin(endpoint),
    uidSite: uidSite ?? '',
    endpointClass: classifyErr998Endpoint(normalizedEndpoint),
    // 上報欄位：能走到 998 分支必然無 HTTP response（有 status 一律不進此分支），固定為 false
    hasResponse: false,
  }
}

// FE-1：時間窗建議 5～15 分鐘，取中間值 10 分鐘。以模組層級 Map 實作，僅 client 端隨分頁存續，
// 比照 suppressAuthError.js 的既有慣例，SSR render 路徑略過，避免模組層級狀態隨 Node process 跨請求累積。
const SELF_DEDUPE_WINDOW_MS = 10 * 60 * 1000
const selfDedupeWindows = new Map()

// FE-1：/self 時間窗去重 —— 同一鑰匙（站台 + API 線 + self + 使用者）在時間窗內只允許送出 Sentry 一次；
// 之後的呼叫回傳 allow=false，並累計 suppressedCount，供有送出的那一筆記錄「被合併掉幾次」。
export function checkErr998SelfDedupe(key) {
  if (!import.meta.client) {
    return { allow: true, suppressedCount: 0 }
  }
  const now = Date.now()
  // 順手清掉過期 entry，避免長駐 server process / 分頁造成 Map 無限增長
  if (selfDedupeWindows.size > 500) {
    for (const [k, v] of selfDedupeWindows) {
      if (now - v.firstAt > SELF_DEDUPE_WINDOW_MS) selfDedupeWindows.delete(k)
    }
  }
  const existing = selfDedupeWindows.get(key)
  if (!existing || now - existing.firstAt > SELF_DEDUPE_WINDOW_MS) {
    // 開新窗口前，把上一個窗口累積的 suppressedCount 帶到這次要送出的報告上，
    // 否則故障期間被抑制的次數會在窗口交界處直接消失，看不出真實故障規模
    const carriedSuppressedCount = existing?.suppressedCount ?? 0
    selfDedupeWindows.set(key, { firstAt: now, suppressedCount: 0 })
    return { allow: true, suppressedCount: carriedSuppressedCount }
  }
  existing.suppressedCount += 1
  return { allow: false, suppressedCount: existing.suppressedCount }
}
